import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { Google as GoogleIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const SignInDialog = ({ open, onClose }) => {
  const { signInWithGoogle } = useAuth();
  const { t, isArabic } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      setError('');
      await signInWithGoogle();
      onClose();
    } catch (error) {
      // Use the enhanced error message from AuthContext
      setError(error.message || t('Failed to sign in. Please try again.', 'فشل في تسجيل الدخول. يرجى المحاولة مرة أخرى.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          direction: isArabic ? 'rtl' : 'ltr'
        }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <Typography variant="h5" component="h2" fontWeight="bold">
          {t('Sign In', 'تسجيل الدخول')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('Sign in to rate lecturers and access your reviews', 'سجل دخولك لتقييم المحاضرين والوصول إلى  تقييماتك السابقة ')}
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ px: 3, py: 2 }}>
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 2 }}
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        )}
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Button
            variant="outlined"
            size="large"
            startIcon={loading ? <CircularProgress size={20} /> : <GoogleIcon />}
            onClick={handleGoogleSignIn}
            disabled={loading}
            sx={{
              py: 1.5,
              borderColor: '#4285f4',
              color: '#4285f4',
              '&:hover': {
                borderColor: '#3367d6',
                backgroundColor: 'rgba(66, 133, 244, 0.04)'
              },
              '&:disabled': {
                borderColor: '#ccc',
                color: '#ccc'
              }
            }}
          >
            {loading 
              ? t('Signing in...', 'جاري تسجيل الدخول...') 
              : t('Continue with Google', 'المتابعة مع جوجل')
            }
          </Button>
          
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
            {t(
              'By signing in, you agree to our Terms of Service and Privacy Policy',
              'بتسجيل الدخول، فإنك توافق على شروط الخدمة وسياسة الخصوصية'
            )}
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} color="inherit">
          {t('Cancel', 'إلغاء')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SignInDialog; 